/* inno-imgload.js — a real loading percentage in place of an image that hasn't arrived yet.

   The browser gives no way to read how far along an <img> is, so a truthful percentage means
   owning the transfer: images ship with `data-inno-src` instead of `src`, and this streams the
   bytes itself, counting them against Content-Length, then hands the finished blob to the tag.
   One request, not two — the <img> never fetches on its own because it has no src until the
   bytes are already in hand.

   Everything about it degrades to a plain load: no fetch, no streams, a cross-origin refusal, a
   missing Content-Length, a stall, or simply never being swept — each falls through to setting
   `src` directly. A picture that loads without a readout is a small loss; a picture that never
   loads because the readout machinery failed is not acceptable, so every path ends in an image.  */
(function () {
  'use strict';

  var LEAD = 400;          // start this far before the image reaches the viewport
  var STALL_MS = 12000;    // a transfer this slow has failed, whatever the network thinks
  var SWEEP_MS = 400;      // backstop cadence — scroll drives it, this catches everything else
  var CAN_STREAM = !!(window.fetch && window.ReadableStream && window.URL && URL.createObjectURL);

  function css() {
    if (document.getElementById('inno-imgload-css')) return;
    var s = document.createElement('style');
    s.id = 'inno-imgload-css';
    s.textContent =
      '.inno-ph{position:absolute;inset:0;z-index:2;display:flex;align-items:center;' +
        'justify-content:center;pointer-events:none;background:rgba(10,10,10,.82);' +
        'font-family:"IBM Plex Mono",ui-monospace,SFMono-Regular,Menlo,monospace;' +
        'font-size:11px;letter-spacing:.22em;text-transform:uppercase;' +
        'font-variant-numeric:tabular-nums;color:#E8E4DC;' +
        'transition:opacity .3s ease;}' +
      '.inno-ph.is-done{opacity:0;}';
    (document.head || document.documentElement).appendChild(s);
  }

  function addPlaceholder(img) {
    var host = img.parentNode;
    if (!host || host.nodeType !== 1) return null;
    // the readout is absolutely placed, so its host has to be a containing block
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    var el = document.createElement('span');
    el.className = 'inno-ph';
    el.setAttribute('aria-hidden', 'true');
    el.textContent = '0%';
    host.appendChild(el);
    return el;
  }

  function clearPlaceholder(ph) {
    if (!ph) return;
    ph.classList.add('is-done');
    setTimeout(function () { if (ph.parentNode) ph.parentNode.removeChild(ph); }, 340);
  }

  // last resort — let the tag fetch it the ordinary way
  function plain(img, url, ph) {
    if (img._innoPlain) return;
    img._innoPlain = true;
    img.addEventListener('load', function () { clearPlaceholder(ph); }, { once: true });
    img.addEventListener('error', function () { clearPlaceholder(ph); }, { once: true });
    // the ordinary path keeps srcset — that's the browser doing its own job properly — but the
    // lazy deferral still has to go, or a tag we're actively loading can sit and wait
    img.loading = 'eager';
    img.src = url;
  }

  /* A responsive image can't just be handed a blob: `srcset` outranks `src`, so the browser
     would ignore the bytes we fetched and go get its own — two transfers, and the readout
     measuring the wrong one. Rather than strip srcset and serve everyone the smallest file
     (mobile would get a soft 500px image blown up to full width, which is exactly what the
     srcset exists to prevent), we make the same choice the browser would: the narrowest
     candidate that still covers this element's width at this display's pixel ratio. */
  function pickSrc(img) {
    var ss = img.getAttribute('srcset');
    var base = img.getAttribute('data-inno-src');
    if (!ss) return base;
    var r = img.getBoundingClientRect();
    var want = Math.max(r.width || 0, 1) * (window.devicePixelRatio || 1);
    var best = null;
    ss.split(',').forEach(function (part) {
      var m = part.trim().match(/^(\S+)\s+(\d+)w$/);
      if (!m) return;
      var cand = { url: m[1], w: parseInt(m[2], 10) };
      if (!best) { best = cand; return; }
      if (cand.w >= want) { if (best.w < want || cand.w < best.w) best = cand; }
      else if (best.w < want && cand.w > best.w) best = cand;
    });
    return best ? best.url : base;
  }

  // the tag has no reason to defer once the bytes are in hand, and srcset must not be allowed
  // to override the source we just paid for
  function adopt(img, src) {
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.loading = 'eager';
    img.src = src;
  }

  /* An image that carries its own src is left alone: the browser started it during HTML parse,
     which is the whole point, and taking it over here would mean a second transfer. The readout
     still shows — it paces the arrival instead of measuring it, and lands on 100% when the
     picture actually decodes. Cosmetic by design, and it says so. */
  function adoptNative(img) {
    if (img._innoNative) return;
    img._innoNative = true;
    if (img.complete && img.naturalWidth > 0) return;       // already here: nothing to announce
    var ph = addPlaceholder(img);
    var done = function () { if (ph) ph.textContent = '100%'; clearPlaceholder(ph); };
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
    var t0 = (window.performance && performance.now) ? performance.now() : Date.now();
    (function tick() {
      if (!ph || !ph.isConnected) return;
      if (img.complete) { done(); return; }
      var now = (window.performance && performance.now) ? performance.now() : Date.now();
      ph.textContent = Math.round(Math.min(0.94, (now - t0) / 1100) * 100) + '%';
      setTimeout(tick, 70);
    })();
  }

  function start(img) {
    var url = pickSrc(img);
    if (!url || img._innoStarted) return;
    img._innoStarted = true;
    img.removeAttribute('data-inno-src');

    var ph = addPlaceholder(img);
    if (!CAN_STREAM) { plain(img, url, ph); return; }

    var stalled = setTimeout(function () { plain(img, url, ph); }, STALL_MS);
    // and a second net: if the tag has taken a source but still hasn't decoded, fall back to a
    // normal load rather than leave a hole where the picture should be
    setTimeout(function () {
      if (!img._innoPlain && !(img.complete && img.naturalWidth > 0)) plain(img, url, ph);
    }, STALL_MS + 3000);

    fetch(url, { credentials: 'omit' }).then(function (res) {
      if (!res.ok || !res.body) throw new Error('unusable response');
      var total = parseInt(res.headers.get('content-length') || '0', 10);
      var reader = res.body.getReader();
      var chunks = [], got = 0;
      return (function pump() {
        return reader.read().then(function (r) {
          if (r.done) {
            clearTimeout(stalled);
            if (ph) ph.textContent = '100%';
            var obj = URL.createObjectURL(new Blob(chunks, { type: res.headers.get('content-type') || 'image/jpeg' }));
            // revoke as soon as the tag has decoded it, or the blob stays resident for the life
            // of the page — across a lattice of thirty-odd photos that adds up quickly
            img.addEventListener('load', function () { URL.revokeObjectURL(obj); clearPlaceholder(ph); }, { once: true });
            img.addEventListener('error', function () { URL.revokeObjectURL(obj); plain(img, url, ph); }, { once: true });
            adopt(img, obj);
            return;
          }
          chunks.push(r.value);
          got += r.value.length;
          // Without Content-Length there is no honest fraction to show, so it counts bytes
          // instead of inventing a percentage.
          if (ph) {
            ph.textContent = total
              ? Math.min(99, Math.floor((got / total) * 100)) + '%'
              : Math.floor(got / 1024) + 'KB';
          }
          return pump();
        });
      })();
    }).catch(function () {
      clearTimeout(stalled);
      plain(img, url, ph);
    });
  }

  function sweep() {
    var own = document.querySelectorAll('img[data-inno-native]');
    for (var n = 0; n < own.length; n++) adoptNative(own[n]);
    var pend = document.querySelectorAll('img[data-inno-src]');
    if (!pend.length) return;
    var h = window.innerHeight || 0;
    for (var i = 0; i < pend.length; i++) {
      var r = pend[i].getBoundingClientRect();
      // an element with no box yet (inside a hidden overlay) has nothing to measure — leave it
      // for a later sweep rather than loading the whole catalogue at once
      if (!r.width && !r.height) continue;
      if (r.bottom > -LEAD && r.top < h + LEAD) start(pend[i]);
    }
  }

  var raf = 0, tick = 0, sweptAt = 0;
  function sweepOnce() { sweptAt = performance.now(); sweep(); }
  function schedule() {
    if (!raf) raf = requestAnimationFrame(function () { raf = 0; sweepOnce(); });
    clearTimeout(tick);
    // the timer is the backstop for environments where rAF never runs — if a frame has already
    // swept in the meantime there is nothing left for it to do, so it stops doubling the work
    tick = setTimeout(function () { if (performance.now() - sweptAt > 100) sweepOnce(); }, 120);
  }

  function boot() {
    css();
    sweep();
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    /* The backstop only measures while something is actually waiting. It used to read a rect for
       every pending image every 400ms for the life of the page — a forced layout, forever. */
    setInterval(function () {
      if (document.querySelector('img[data-inno-src]')) sweepOnce();
    }, SWEEP_MS);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.innoImgLoad = start;           // let a caller start one immediately
})();
